/**
 * Customer self-serve agent-procurement RPC definitions (CLA-605).
 *
 * A signed-in customer member adds an agent to their OWN customer with
 * provisioning gated on payment success. Four endpoints, all under the standard
 * token/Clerk {@link AuthMiddleware} (NOT `AdminMiddleware`), namespaced `agents.`:
 *
 *  - `procureQuote` — resolve the actor's customer and quote an add: the resolved
 *    per-seat monthly price (T1 {@link AgentPriceService}), the prorated
 *    immediate charge preview (T2 `SeatBillingService.previewSeatAdd`), a $0
 *    install fee (self-serve adds carry no setup fee), the plan and currency.
 *  - `procure` — payment-first. Charge the prorated seat delta on the saved
 *    payment method via `SeatBillingService.addSeat`; on `paid` create the agent
 *    row (self-serve ⇒ `intakeId` omitted) and return it; on `requires_action`
 *    DEFER row creation and return the SCA client secret + invoice id; a decline
 *    fails typed and creates nothing.
 *  - `confirmProcure` — post-SCA completion. Cross-check the paid invoice belongs
 *    to the actor customer's subscription, then create the agent (idempotent via
 *    the invoice's `agentInstallationId` metadata ledger).
 *  - `installHandle` — customer-scoped wrapper over `AgentService.mintInstallLink`:
 *    assert the agent belongs to the actor's customer, then mint the Mac curl
 *    one-liner / dispatch VPS provisioning.
 *
 * Reseller-attributed customers are rejected from the mutating endpoints — their
 * procurement path is the reseller flow (a sibling ticket), not platform seat
 * billing.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { BrowserKind, CloudProvider, DeploymentType } from "@fm/db/models/AgentInstallation";
import { AgentRatePlan } from "@fm/db/models/Customer";
import { Schema } from "effect";
import { AgentsMintInstallLinkResult, AgentSummary } from "../admin/agents.rpc.ts";
import { InstallTokenCipherError, WorkflowDispatchFailedError } from "../admin/installs.rpc.ts";
import { AuthMiddleware } from "../auth.tags.ts";
import {
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  SeatBillingError,
  SubscriptionError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";
import { SelfServePlatform } from "./self-serve.schemas.ts";

// ============================================================================
// procureQuote
// ============================================================================

/**
 * Quote input — the deployment shape the customer wants to add. `region` is
 * required for `cloud-vps` (validated server-side, mirroring
 * {@link AgentService.create}); `cloudProvider` is optional (defaults apply on
 * create).
 *
 * `customerId` is a SELECTOR among the caller's own memberships, never an
 * identifier: when present it is membership-validated server-side (a non-member
 * or nonexistent id is {@link Forbidden}, existence never leaked); when omitted
 * the server resolves the default org (subscription-bearing, else first
 * membership) — the same rule the fleet/plan-badge reads use (CLA-705), so the
 * quote matches the dashboard the customer is looking at.
 *
 * @since 1.0.0
 */
export const ProcureQuoteInput = Schema.Struct({
  deploymentType: DeploymentType,
  cloudProvider: Schema.optional(CloudProvider),
  region: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type ProcureQuoteInput = Schema.Schema.Type<typeof ProcureQuoteInput>;

/**
 * Quote result — all amounts integer cents.
 *
 *  - `monthlyAmountCents` — the resolved per-seat monthly price (T1).
 *  - `prorationPreviewCents` — the prorated immediate charge for adding one seat
 *    now (T2 `previewSeatAdd`, non-mutating).
 *  - `installFeeCents` — always 0: self-serve adds carry no one-time setup fee.
 *  - `plan` — the customer's billing cadence.
 *  - `currency` — always `usd`.
 *
 * @since 1.0.0
 */
export const ProcureQuote = Schema.Struct({
  monthlyAmountCents: Schema.Int,
  prorationPreviewCents: Schema.Int,
  installFeeCents: Schema.Literal(0),
  plan: AgentRatePlan,
  currency: Schema.Literal("usd"),
});
export type ProcureQuote = Schema.Schema.Type<typeof ProcureQuote>;

// ============================================================================
// procure / confirmProcure
// ============================================================================

/**
 * Procure input — the deployment shape plus an optional agent name. Like
 * {@link ProcureQuoteInput}, `customerId` is a SELECTOR among the caller's own
 * memberships (membership-validated server-side, never trusted as an
 * identifier — a non-member or nonexistent id is {@link Forbidden} before any
 * billing/agent-create call); omitted resolves the shared default org (CLA-705).
 *
 * @since 1.0.0
 */
export const ProcureInput = Schema.Struct({
  deploymentType: DeploymentType,
  cloudProvider: Schema.optional(CloudProvider),
  region: Schema.optional(Schema.String),
  name: Schema.optional(Schema.NonEmptyTrimmedString),
  // Which browser to provision (chrome | camoufox). Omitted = "chrome"
  // (persisted default). The `BrowserKind` allowlist rejects any off-list
  // value; the quote/price is unchanged by this choice (v1: camoufox is
  // simply cheaper for us to run — see CLA-659).
  browser: Schema.optional(BrowserKind),
  customerId: Schema.optional(Schema.String),
});
export type ProcureInput = Schema.Schema.Type<typeof ProcureInput>;

/**
 * The payment outcome of an add:
 *
 *  - `paid` — the prorated charge settled immediately off-session;
 *    `proratedChargeCents` is what was charged.
 *  - `requires_action` — the charge needs Strong Customer Authentication;
 *    `clientSecret` is confirmed on-session by the present customer and
 *    `invoiceId` is passed back to `confirmProcure` once it settles.
 *
 * @since 1.0.0
 */
export const ProcurePayment = Schema.Union(
  Schema.Struct({
    status: Schema.Literal("paid"),
    proratedChargeCents: Schema.Int,
  }),
  Schema.Struct({
    status: Schema.Literal("requires_action"),
    clientSecret: Schema.String,
    invoiceId: Schema.String,
  }),
);
export type ProcurePayment = Schema.Schema.Type<typeof ProcurePayment>;

/**
 * Procure result — the created agent plus the payment outcome. `agent` is `null`
 * on `requires_action`: row creation is DEFERRED until payment settles, so
 * nothing exists until `confirmProcure` finishes the job.
 *
 * @since 1.0.0
 */
export const ProcureResult = Schema.Struct({
  agent: Schema.NullOr(AgentSummary),
  payment: ProcurePayment,
});
export type ProcureResult = Schema.Schema.Type<typeof ProcureResult>;

/**
 * Confirm input — the SCA-completed `invoiceId` plus the same deployment shape
 * as the original `procure` call, so the deferred agent is materialised with the
 * customer's intended configuration. Idempotent: a repeat call for an invoice
 * already stamped with an `agentInstallationId` returns that existing agent.
 *
 * `customerId` carries the same org selection as the original `procure` call
 * (SELECTOR among memberships, membership-validated server-side); omitted
 * resolves the shared default org (CLA-705).
 *
 * @since 1.0.0
 */
export const ConfirmProcureInput = Schema.Struct({
  invoiceId: Schema.NonEmptyTrimmedString,
  deploymentType: DeploymentType,
  cloudProvider: Schema.optional(CloudProvider),
  region: Schema.optional(Schema.String),
  name: Schema.optional(Schema.NonEmptyTrimmedString),
  // Carried alongside the deployment shape so the SCA-deferred agent is
  // materialised with the browser the customer chose at `procure` time.
  browser: Schema.optional(BrowserKind),
  customerId: Schema.optional(Schema.String),
  // Present when the deferred agent is an ADOPTION of a box the customer
  // already runs rather than a fresh install — carried so the SCA-deferred
  // agent is materialised down the adopt path (CLA-1041 part P).
  adopt: Schema.optional(Schema.Struct({
    platform: SelfServePlatform,
    name: Schema.optional(Schema.NonEmptyTrimmedString),
    enableRemoteSupport: Schema.Boolean,
  })),
});
export type ConfirmProcureInput = Schema.Schema.Type<typeof ConfirmProcureInput>;

// ============================================================================
// adopt
// ============================================================================

/**
 * Adopt input — self-serve onboarding of a machine the customer ALREADY runs.
 * There is no deployment shape to choose: the box exists, so the caller names
 * only its {@link SelfServePlatform}, an optional display name, and whether the
 * operator may remote-support it.
 *
 * `customerId` is a SELECTOR among the caller's memberships (validated
 * server-side via `resolveActorCustomerRequired`); omitted resolves the shared
 * default org (CLA-705).
 *
 * @since 1.0.0
 */
export const AgentsAdoptInput = Schema.Struct({
  customerId: Schema.optional(Schema.String),
  name: Schema.optional(Schema.NonEmptyTrimmedString),
  platform: SelfServePlatform,
  enableRemoteSupport: Schema.Boolean,
});
export type AgentsAdoptInput = Schema.Schema.Type<typeof AgentsAdoptInput>;

/**
 * Adopt result — the adopted agent plus the one-liner the customer pastes on
 * the box, and the payment outcome. As with `procure`, everything but
 * `payment` is `null` on `requires_action`: row creation is DEFERRED until the
 * charge settles, so `confirmProcure` (with its `adopt` block) mints the
 * command instead.
 *
 * @since 1.0.0
 */
export const AgentsAdoptResult = Schema.Struct({
  agent: Schema.NullOr(AgentSummary),
  adoptCommand: Schema.NullOr(Schema.String),
  installTokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  payment: ProcurePayment,
});
export type AgentsAdoptResult = Schema.Schema.Type<typeof AgentsAdoptResult>;

// ============================================================================
// installHandle
// ============================================================================

/**
 * Install-handle input — the agent to mint the install hand-off for. The agent
 * must belong to a customer the caller is a member of (ownership enforced
 * server-side; a non-owned agent is {@link Forbidden}).
 *
 * @since 1.0.0
 */
export const ProcureInstallHandleInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
});
export type ProcureInstallHandleInput = Schema.Schema.Type<typeof ProcureInstallHandleInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Customer self-serve agent-procurement RPC group. Every endpoint requires a
 * valid token/Clerk session via {@link AuthMiddleware}. Tags are namespaced
 * under `agents.`.
 *
 * @since 1.0.0
 */
export class AgentsProcureRpcs extends RpcGroup.make(
  Rpc.make("agents.procureQuote", {
    payload: ProcureQuoteInput,
    success: ProcureQuote,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      SeatBillingError,
      SubscriptionError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("agents.procure", {
    payload: ProcureInput,
    success: ProcureResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      SeatBillingError,
      SubscriptionError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("agents.adopt", {
    payload: AgentsAdoptInput,
    success: AgentsAdoptResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      SeatBillingError,
      SubscriptionError,
      RecordNotFoundError,
      DatabaseError,
      // Adoption mints an install token inline (unlike `procure`, which
      // hands off to `installHandle`), so the cipher/dispatch failures of the
      // mint path are part of this endpoint's contract.
      InstallTokenCipherError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("agents.confirmProcure", {
    payload: ConfirmProcureInput,
    success: ProcureResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      ValidationError,
      SeatBillingError,
      SubscriptionError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("agents.installHandle", {
    payload: ProcureInstallHandleInput,
    success: AgentsMintInstallLinkResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallTokenCipherError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AuthMiddleware),
) {}
